import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, Check, Camera, Layers, KeyRound, Server, Palette } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import HeroGallery from '../components/HeroGallery';
import FeOffer from '../components/FeOffer';
import { Button } from '../components/ui/button';
import { HOW_STEPS, STYLE_TEMPLATES, USE_CASES, INFLUENCER_SHOTS, WHITELABEL_POINTS, COMPARISON } from '../mock';
import { api } from '../lib/api';

// Cuantas sesiones hay de verdad, contadas del catalogo. La cifra estaba
// escrita a mano en cinco sitios y ya se habia quedado corta: la portada decia
// cien y habia ciento tres. Un numero que el comprador puede contar no puede
// depender de que alguien se acuerde de actualizar una frase.
const SHOWN_IN_GRID = 21;

export default function Landing() {
  const navigate = useNavigate();
  const [count, setCount] = useState(null);
  useEffect(() => {
    api.publicConfig().then((c) => setCount(c?.recipe_count || null)).catch(() => {});
  }, []);
  const rest = count && count > SHOWN_IN_GRID ? count - SHOWN_IN_GRID : null;

  return (
    <div className="App min-h-screen bg-background text-foreground">
      <Navbar />

      {/* HERO */}
      <section className="relative flex min-h-[100svh] items-center justify-center overflow-hidden" data-testid="hero-section">
        <HeroGallery />
        <div className="absolute inset-0 bg-gradient-to-b from-background/75 via-background/45 to-background" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 45%, rgba(14,14,16,0.2), rgba(14,14,16,0.94))' }} />

        <div className="relative z-10 mx-auto max-w-4xl px-5 pt-24 text-center">
          <div className="fade-up inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs text-white/80 backdrop-blur-sm">
            <Sparkles size={13} className="text-node-style" />
            One studio, a hundred sessions
          </div>

          <h1 className="fade-up mt-7 font-serif-display text-[2.5rem] font-500 leading-[1.03] tracking-tight text-white sm:text-6xl md:text-7xl" style={{ animationDelay: '80ms' }}>
            A hundred photo shoots.<br />
            <span className="italic text-white/90">One upload each.</span>
          </h1>

          <p className="fade-up mx-auto mt-6 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg" style={{ animationDelay: '160ms' }}>
            Your product on a clean seamless. Your cap on a model. The headshot your profile has
            been missing. Your grandmother&apos;s creased photograph, mended. Each one is a session
            that already knows its own camera, lens and light — you bring the one part only you have.
          </p>

          <div className="fade-up mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row" style={{ animationDelay: '240ms' }}>
            <Button onClick={() => navigate('/register')} data-testid="hero-cta-primary" className="group h-12 rounded-full bg-white px-7 text-sm font-medium text-black hover:bg-white/90">
              Start free
              <ArrowRight size={16} className="ml-1.5 transition-transform group-hover:translate-x-0.5" />
            </Button>
            <a href="#use-cases" data-testid="hero-cta-secondary" className="inline-flex h-12 items-center rounded-full border border-white/20 bg-white/5 px-7 text-sm text-white backdrop-blur-sm transition-colors hover:bg-white/10">
              See what it makes
            </a>
          </div>
          <p className="fade-up mt-5 text-xs text-white/50" style={{ animationDelay: '320ms' }}>150 free credits &middot; No card required</p>
        </div>
      </section>

      {/* WHAT YOU CAN MAKE */}
      <section id="use-cases" className="relative mx-auto max-w-7xl px-5 py-24 md:px-8" data-testid="use-cases-section">
        <div className="max-w-2xl">
          <p className="text-sm font-medium uppercase tracking-widest text-node-model">What you can make</p>
          <h2 className="mt-3 font-serif-display text-3xl leading-tight text-white sm:text-4xl md:text-5xl">
            Four things, and you already have the input for all of them.
          </h2>
          <p className="mt-4 max-w-lg text-base leading-relaxed text-muted-foreground">Whether you sell on Etsy, eBay or Amazon, run a brand, need one good portrait of yourself, or have a shoebox of photographs worth saving.</p>
        </div>
        <div className="mt-12 grid gap-4 lg:grid-cols-12">
          {USE_CASES.map((u) => (
            <div key={u.key} data-testid={`use-case-${u.key}`} className={`group relative overflow-hidden rounded-2xl border border-border bg-card ${u.span}`}>
              <div className="absolute inset-0">
                {u.video ? (
                  <video
                    src={u.video}
                    poster={u.src}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    data-testid={`use-case-video-${u.key}`}
                    className="h-full w-full object-cover opacity-55 transition-all duration-500 group-hover:scale-105 group-hover:opacity-70"
                  />
                ) : (
                  <img src={u.src} alt={u.name} className="h-full w-full object-cover opacity-45 transition-all duration-500 group-hover:scale-105 group-hover:opacity-60" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              </div>
              <div className="relative flex min-h-[240px] flex-col justify-end p-6">
                <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-white/15 bg-black/40 px-2.5 py-1 text-[11px] text-white/80 backdrop-blur-sm">
                  <Camera size={11} style={{ color: u.accent }} /> Photo
                </span>
                <h3 className="mt-3 text-xl font-semibold text-white">{u.name}</h3>
                <p className="mt-1.5 max-w-sm text-sm leading-relaxed text-white/70">{u.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PERSISTENT INFLUENCER */}
      <section className="relative border-y border-border bg-[#0B0B0D]" data-testid="persistent-influencer-section">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 py-24 md:px-8 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-sm font-medium uppercase tracking-widest text-node-model">The same person, every shoot</p>
            <h2 className="mt-3 font-serif-display text-3xl leading-tight text-white sm:text-4xl md:text-5xl">One face. Every angle.</h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">
              Add a few photos of a face — yours, or a model you invent — and every session after that
              uses all of them. More reference photos, closer likeness. The same person turns up in every
              angle and every outfit, which is what makes a catalogue look like a catalogue.
            </p>
            <ul className="mt-6 space-y-3">
              {['Every reference photo used, not just one', 'Wear your product, or hold it up to camera', 'Locked identity across the whole set'].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-white/75">
                  <Check size={16} className="mt-0.5 shrink-0 text-node-model" />{t}
                </li>
              ))}
            </ul>
            <Button onClick={() => navigate('/register')} className="group mt-8 h-11 rounded-full bg-white px-6 text-sm font-medium text-black hover:bg-white/90">
              Create your model <ArrowRight size={15} className="ml-1.5 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {INFLUENCER_SHOTS.map((src, i) => (
              <div key={i} className={`overflow-hidden rounded-2xl border border-white/10 ${i % 2 === 1 ? 'mt-6' : ''}`} style={{ boxShadow: i === 0 ? '0 0 30px rgba(167,139,250,0.18)' : 'none' }}>
                <img src={src} alt="Consistent AI influencer" className="aspect-[3/4] w-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="relative mx-auto max-w-7xl px-5 py-24 md:px-8" data-testid="how-it-works-section">
        <div className="max-w-2xl">
          <p className="text-sm font-medium uppercase tracking-widest text-node-style">How it works</p>
          <h2 className="mt-3 font-serif-display text-3xl leading-tight text-white sm:text-4xl md:text-5xl">From a phone photo to a listing, in three moves.</h2>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {HOW_STEPS.map((s) => (
            <div key={s.n} className="group relative rounded-2xl border border-border bg-card p-7 transition-colors hover:border-white/25">
              <div className="flex items-center gap-3">
                <span className="font-serif-display text-3xl" style={{ color: s.color }}>{s.n}</span>
                <span className="h-px flex-1" style={{ background: `linear-gradient(90deg, ${s.color}55, transparent)` }} />
              </div>
              <h3 className="mt-5 text-lg font-semibold text-white">{s.title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* COMPARISON: Idolaris vs the old way */}
      <section id="compare" className="relative border-y border-border bg-[#0B0B0D]" data-testid="comparison-section">
        <div className="mx-auto max-w-6xl px-5 py-24 md:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-medium uppercase tracking-widest text-node-product">The maths</p>
            <h2 className="mt-3 font-serif-display text-3xl leading-tight text-white sm:text-4xl md:text-5xl">Against booking a photographer.</h2>
            <p className="mx-auto mt-4 max-w-lg text-base leading-relaxed text-muted-foreground">A shoot costs a day and a fee. A reshoot costs both again. This costs cents and finishes while you wait.</p>
          </div>

          <div className="mt-12 overflow-hidden rounded-2xl border border-border">
            <div className="grid grid-cols-4 border-b border-border bg-card text-xs font-semibold uppercase tracking-wider text-white/50 sm:text-sm">
              <div className="p-4"></div>
              <div className="p-4 text-center text-white">
                <span className="inline-flex items-center gap-1.5"><Sparkles size={14} className="text-node-style" /> Idolaris</span>
              </div>
              <div className="p-4 text-center">A photographer</div>
              <div className="p-4 text-center">A studio</div>
            </div>
            {COMPARISON.rows.map((r, i) => (
              <div key={r.label} data-testid={`compare-row-${i}`} className={`grid grid-cols-4 items-center text-sm ${i % 2 === 0 ? 'bg-background' : 'bg-card/40'}`}>
                <div className="p-4 text-xs font-medium text-white/70 sm:text-sm">{r.label}</div>
                <div className="flex items-center justify-center gap-1.5 p-4 text-center text-node-environment">
                  <Check size={14} className="hidden shrink-0 sm:inline" />
                  <span className="text-xs font-medium text-white sm:text-sm">{r.idolaris}</span>
                </div>
                <div className="p-4 text-center text-xs text-muted-foreground sm:text-sm">{r.ugc}</div>
                <div className="p-4 text-center text-xs text-muted-foreground sm:text-sm">{r.agency}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SESSIONS — the catalogue is the argument, so it goes on the page */}
      <section id="sessions" className="relative mx-auto max-w-7xl px-5 py-24 md:px-8" data-testid="sessions-section">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.15fr] lg:items-center">
          <div>
            <p className="text-sm font-medium uppercase tracking-widest text-node-product">Photo sessions</p>
            <h2 className="mt-3 font-serif-display text-3xl leading-tight text-white sm:text-4xl md:text-5xl">
              A hundred shoots that already know how they are lit.
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">
              Not prompts to write. Each session carries its own camera, lens, lighting and framing,
              and the physics of the thing being photographed — an e-reader is matte and grey and
              never glossy, a paperback is paper and never a screen, a restoration keeps the grain
              it was printed with. Pick one, drop your photo in, and it shoots itself.
            </p>
            <Button onClick={() => navigate('/register')} className="group mt-8 h-11 rounded-full bg-white px-6 text-sm font-medium text-black hover:bg-white/90">
              Browse the sessions <ArrowRight size={15} className="ml-1.5 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
            {[
              ['Packshot', '#F5A623'], ['Flat lay', '#F5A623'], ['In the hand', '#F5A623'],
              ['On a model', '#A78BFA'], ['Try-on', '#A78BFA'], ['Detail crop', '#A78BFA'],
              ['LinkedIn headshot', '#22D3EE'], ['ID photo', '#22D3EE'], ['Press shot', '#22D3EE'],
              ['Travel', '#34D399'], ['Food', '#34D399'], ['Podcast interview', '#34D399'],
              ['Press conference', '#FFFFFF'], ['Receiving an award', '#FFFFFF'], ['Seminar talk', '#FFFFFF'],
              ['Restore an old photo', '#F9A8D4'], ['Colour a black and white', '#F9A8D4'], ['Rescue a blurry face', '#F9A8D4'],
              ['Splash campaign shot', '#F5A623'], ['Book review', '#22D3EE'], ['E-book on a Kindle', '#22D3EE'],
            ].map(([name, accent]) => (
              <div key={name} className="rounded-xl border border-border bg-card px-3.5 py-3 transition-colors hover:border-white/25">
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: accent, boxShadow: `0 0 10px ${accent}66` }} />
                <p className="mt-2.5 text-[13px] font-medium leading-snug text-white/85">{name}</p>
              </div>
            ))}
            <div className="col-span-2 flex items-center justify-center rounded-xl border border-dashed border-[#2A2A30] px-3.5 py-3 sm:col-span-3">
              <span className="inline-flex items-center gap-2 text-[13px] text-white/45">
                <Layers size={13} /> {rest ? `and ${rest} more` : 'and many more'}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* STYLE TEMPLATES */}
      <section id="styles" className="relative border-y border-border bg-[#0B0B0D]" data-testid="style-templates-section">
        <div className="mx-auto max-w-7xl px-5 py-24 md:px-8">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div className="max-w-2xl">
              <p className="text-sm font-medium uppercase tracking-widest text-node-model">Looks</p>
              <h2 className="mt-3 font-serif-display text-3xl leading-tight text-white sm:text-4xl md:text-5xl">Sixteen looks to shoot any session in.</h2>
            </div>
            <p className="max-w-sm text-sm text-muted-foreground">The session decides the shot; the look decides the mood. Real camera, lens and lighting choices behind each one.</p>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {STYLE_TEMPLATES.map((t) => (
              <div key={t.slug} className="group relative overflow-hidden rounded-xl border border-border bg-card p-5 transition-colors hover:border-white/25">
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: t.accent, boxShadow: `0 0 12px ${t.accent}77` }} />
                <h3 className="mt-4 text-sm font-semibold text-white">{t.name}</h3>
                <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground line-clamp-2">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHITELABEL / BYOK */}
      <section id="whitelabel" className="relative border-b border-border bg-background" data-testid="whitelabel-section">
        <div className="mx-auto max-w-7xl px-5 py-24 md:px-8">
          <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <p className="text-sm font-medium uppercase tracking-widest text-node-environment">Self-hosted &amp; whitelabel</p>
              <h2 className="mt-3 font-serif-display text-3xl leading-tight text-white sm:text-4xl md:text-5xl">Your studio. Your keys. Your margins.</h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">
                Connect your own provider account and the photographs cost you cents, billed to you at
                cost, with nobody metering you in between. Or buy credits and skip the setup. Either
                way the images are yours to sell.
              </p>
              <div className="mt-7 rounded-xl border border-border bg-[#0E0E10] p-4 font-mono text-xs leading-relaxed text-white/70">
                <p className="flex items-center gap-2 text-white/40"><Server size={13} /> your-instance</p>
                <p className="mt-2"><span className="text-node-environment">$</span> provider_key <span className="text-white/40">→</span> <span className="text-node-style">stored ✓</span></p>
                <p><span className="text-node-environment">$</span> cost_per_photo <span className="text-white/40">→</span> <span className="text-node-product">$0.02</span></p>
                <p><span className="text-node-environment">$</span> monthly_fee <span className="text-white/40">→</span> <span className="text-node-product">$0.00</span></p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {WHITELABEL_POINTS.map((p, i) => (
                <div key={p.title} className="rounded-2xl border border-border bg-card p-6 transition-colors hover:border-white/25">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/5">
                    {[<KeyRound size={16} className="text-node-style" />, <Server size={16} className="text-node-environment" />, <Check size={16} className="text-node-product" />, <Palette size={16} className="text-node-model" />][i]}
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-white">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT OFFER (FE only; upsells live on their own sub-landings) */}
      <section id="pricing" className="relative mx-auto max-w-7xl px-5 py-24 md:px-8" data-testid="product-offer-section">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-medium uppercase tracking-widest text-node-style">Own the engine</p>
          <h2 className="mt-3 font-serif-display text-3xl leading-tight text-white sm:text-4xl md:text-5xl">One-time setup. Own it forever.</h2>
          <p className="mx-auto mt-4 max-w-lg text-base leading-relaxed text-muted-foreground">No monthly fees. Start free, then own the licence and shoot with your own provider account at cost.</p>
        </div>
        <div className="mt-12"><FeOffer /></div>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          Want to scale further? <button onClick={() => navigate('/pricing')} data-testid="see-upgrades-link" className="text-node-style underline-offset-4 hover:underline">See the optional upgrades &rarr;</button>
        </p>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden" data-testid="final-cta-section">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-30 blur-[120px]" style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.6), transparent 70%)' }} />
        <div className="relative mx-auto max-w-7xl px-5 py-28 text-center md:px-8">
          <h2 className="mx-auto max-w-3xl font-serif-display text-4xl leading-[1.05] text-white sm:text-5xl md:text-6xl">What are you photographing first?</h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">A product, a face, or both. Upload a phone photo and see what comes back. No camera, no crew, no prompts.</p>
          <div className="mt-9 flex justify-center">
            <Button onClick={() => navigate('/register')} data-testid="final-cta-button" className="group h-12 rounded-full bg-white px-8 text-sm font-medium text-black hover:bg-white/90">
              Get started now <ArrowRight size={16} className="ml-1.5 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
